import type {ReactNode} from "react";

import {cn} from "@project/design-system-web";

export interface PageSubContainerProps {
  children: ReactNode;
  /**
   * Additional classes appended to the root container.
   */
  className?: string;
}

/**
 * PageSubContainer — semantic layout wrapper for nested routed components (Tabs/Outlets).
 *
 * Designed to sit inside a `<PageContainer>` or a split-panel layout.
 * It does NOT manage padding or scrolling (leaving that to the parent layout),
 * but it DOES enforce the standard responsive vertical rhythm (`gap-4 lg:gap-6`)
 * and ensures the container expands to fill available height for complex children
 * like Data Tables.
 *
 * @example
 * ```tsx
 * export function UserTasksPage() {
 *   return (
 *     <PageSubContainer>
 *       <TableToolbar />
 *       <Table />
 *     </PageSubContainer>
 *   );
 * }
 * ```
 */
export function PageSubContainer({children, className}: PageSubContainerProps) {
  return (
    <div
      className={cn("@container flex h-full min-h-0 flex-1 flex-col gap-2 @lg:gap-4", className)}
    >
      {children}
    </div>
  );
}
